import React, { useState, useRef, useEffect } from 'react'
import TypingAnimation from './TypingAnimation'
import { StaticImage } from 'gatsby-plugin-image'
import { getChatResponse } from '../methods/api'
import chatbotImg from '../img/chatbot.png'
import { Link } from 'gatsby'

const formInitialChatState = (name?: string, lang: 'JA' | 'EN' = 'JA') => {
  const greeting = name
    ? (lang === 'JA' ? `こんにちは${name}` : `Hello ${name}`)
    : (lang === 'JA' ? 'こんにちは' : 'Hello')
  const message = lang === 'JA'
    ? `${greeting}、1F8サービス案内です。クリニック向けホームページ制作・運用についてご質問ください。症状や個人の医療情報は入力しないでください。`
    : `${greeting}, this is the 1F8 service assistant. Ask about our clinic website service. Do not enter symptoms or personal medical information.`
  return [
    {
      sender: 'bot',
      message,
      sentTime: 'just now',
    },
  ]
}

const INTRO_MESSAGE = {
  JA: [
    'こんにちは！クリニック向けホームページ制作・運用についてご案内します。症状や個人の医療情報は入力しないでください。',
    'さて、何のお手伝いができるでしょうか？',
  ],
  EN: [
    'Hello! Ask about our clinic website service. Do not enter symptoms or personal medical information.',
    'So, how can I assist you today?',
  ],
}

const japaneseRegex = /[\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Han}ー々〆〤]+/u

const errorMes = (input: string) => {
  const nErrroMes = `申し訳ありません。現在接続エラーが発生しております。しばらくしてから再度お試しいただくか、後ほど再接続していただけますと幸いです。
  また、より詳細な情報をお求めの場合、ホームページの「お問い合わせ」からご連絡ください`

  const enNErrroMes = 'I\'m sorry, but it seems we are currently experiencing a network error. Please check your network connection. If you require more detailed information, please contact us directly from our homepage contact.'

  return japaneseRegex.test(input) || input === '' ? nErrroMes : enNErrroMes
}

export default function Chatbot({ lang = 'JA' }: { lang?: 'JA' | 'EN' }) {
  const [inputValue, setInputValue] = useState('')
  const [askedName, setAskedName] = useState(true)
  const [name, setName] = useState('')
  const [chatLog, setChatLog] = useState(formInitialChatState(undefined, lang))
  const [isLoading, setIsLoading] = useState(false)
  const [clickChatbot, setClickChatbot] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const chatContainerRef = useRef(null)

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [chatLog])

  if (!isVisible) {
    return null
  }

  const sendMessage = async (messages: any[]) => {
    const input = messages.map((messageObject) => {
      const role = messageObject.sender === 'bot' ? 'assistant' : 'user'
      return { role: role, content: messageObject.message }
    })
    const data = await getChatResponse(input, name, 'chatbot')
      .then(response => response.json())
      .catch((error) => {
        console.error('Error:', error)
        return { success: false }
      })

    setIsLoading(false)
    console.log('res', data)
    const botMessage = data.success ? data.response : errorMes(inputValue)
    setChatLog((prevChatLog: any) => [
      ...prevChatLog,
      { sender: 'bot', message: botMessage },
    ])
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!askedName) {
      setAskedName(true)
      setChatLog(formInitialChatState(name, lang))
      return
    }

    setIsLoading(true)
    setInputValue('')

    const newMessage = {
      message: inputValue,
      direction: 'outgoing',
      sender: 'user',
    }
    const newMessages = [...chatLog, newMessage]

    setChatLog((prevChatLog: any) => [
      ...prevChatLog,
      newMessage,
    ])
    await sendMessage(newMessages)
  }

  const skipName = () => {
    setName('')
    setAskedName(true)
  }

  const handleInquiry = (message: string) => {
    setChatLog((prevChatLog: any) => [
      ...prevChatLog,
      {
        sender: 'bot',
        message: message,
        sentTime: 'just now',
      },
    ])
  }

  const content = {
    businessInquiry: {
      JA: '東京の医院・クリニック向けに、ホームページ制作・改善、SEO、通常更新無制限の運用、患者様からの電話を減らすAIチャットボットを一括で提供しています。',
      EN: 'We build and operate clinic websites in Tokyo, including SEO, unlimited supported routine edits, and an AI chatbot that reduces routine calls and improves patient service.',
    },
    programmingLanguagesInquiry: {
      JA: 'チャットボットは、診療時間、アクセス、予約方法など、クリニックが承認した情報に基づいて回答します。医療判断はせず、必要な質問はスタッフへ案内します。',
      EN: 'The chatbot answers from clinic-approved information about hours, access, booking, and other routine questions. It does not provide medical judgment and routes appropriate questions to staff.',
    },
    newProjectProcessInquiry: {
      JA: '無料サイト診断または20分の無料相談から始めます。現在のサイトを確認し、改善で活かせるか、再構築が必要かを具体的にご提案します。',
      EN: 'Start with a free website assessment or 20-minute consultation. We review the current foundation and recommend either focused improvement or a rebuild.',
    },
    frequentQuestions: {
      JA: 'よくある質問',
      EN: 'Frequent Questions',
    },
    businessInquiryButton: {
      JA: 'どんなサービスですか？',
      EN: 'What does the service include?',
    },
    programmingLanguagesButton: {
      JA: 'チャットボットは何に答えられますか？',
      EN: 'What can the chatbot answer?',
    },
    newProjectProcessButton: {
      JA: '改善と再構築、どちらが必要ですか？',
      EN: 'Do I need Improve or Rebuild?',
    },
    inputPlaceholder: {
      JA: '何をお手伝いしましょうか？',
      EN: 'How can I help you?',
    },
    sendButton: {
      JA: '送信',
      EN: 'Send',
    },
    nameInputPlaceholder: {
      JA: 'お名前は？',
      EN: 'What\'s your name?',
    },
    skipNameButton: {
      JA: '名前をスキップ',
      EN: 'Skip name',
    },
    chatbotIntro: {
      JA: 'サービスについて質問する',
      EN: 'Ask about our service',
    },
    termsOfUse: {
      JA: 'チャットボットに関する利用規約はこちら',
      EN: 'Terms of use for the chatbot',
    },
    namePrompt: {
      JA: '名前を教えてください（スキップしても構いません）',
      EN: 'Please tell us your name (you can skip if you prefer)',
    },
  }

  const handleBusinessInquiry = () => handleInquiry(content.businessInquiry[lang])
  const handleProgrammingLanguagesInquiry = () => handleInquiry(content.programmingLanguagesInquiry[lang])
  const handleNewProjectProcessInquiry = () => handleInquiry(content.newProjectProcessInquiry[lang])

  return (
    <>
      {clickChatbot && askedName ? (
        <div className='max-w-[700px] w-[95%] mx-auto fixed flex flex-col h-3/4 lg:h-2/3 bg-gray-900 rounded-xl shadow-xl bottom-2 sm:bottom-4 justify-center items-stretch md:mx-0 right-0 left-0 md:left-auto md:right-3 z-50'>
          <div className='flex justify-between w-full pt-2 px-2 pb-2'>
            <StaticImage src='../img/chatbot.png'
              className='hover:saturate rounded-full object-cover bg-white shadow-xl'
              placeholder='none'
              height={41} width={42}
              alt='1F8 Logo' />
            <button type='button' className='text-3xl bg-white h-10 w-10 rounded-full' onClick={() => setClickChatbot(!clickChatbot)}><span role='img' aria-label='close'>✖️</span></button>
          </div>
          <div className='flex-grow p-4 sm:p-2 overflow-y-auto h-[400px]' ref={chatContainerRef}>
            <div className='flex flex-col space-y-4'>
              {chatLog.map((message, index) => (
                <div key={`${message} + ${index.toString()}`} className={`flex flex-row ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {message.sender === 'user'
                    ? (
                      <span className='text-purple-500 font-bold mr-2'>{name || (lang === 'JA' ? 'お客様' : 'Customer')}</span>
                    ) : (
                      <img src={chatbotImg}
                        className='rounded-full mr-2'
                        style={{ height: '56px', width: '56px' }}
                        height={56} width={56}
                        alt='Chatbot avatar' />
                    )}
                  <div className={`${message.sender === 'user' ? 'bg-purple-500' : 'bg-gray-500'} h-full rounded-xl px-4 py-2 text-white max-w-sm text-sm`}>
                    {message.message}
                    {index === 0 &&
                      <div className='pt-4 pb-2'>
                        <p>{content.frequentQuestions[lang]}</p>
                        <button
                          type='button'
                          className='mt-2 bg-milkyWhite text-gray-900 px-2 py-2 rounded-xl text-left hover:bg-gray-700 active:bg-milkyWhite'
                          onClick={handleBusinessInquiry}>
                          {content.businessInquiryButton[lang]}
                        </button>
                        <button
                          type='button'
                          className='mt-3 bg-milkyWhite text-gray-900 px-2 py-2 rounded-xl text-left hover:bg-gray-700 active:bg-milkyWhite'
                          onClick={handleProgrammingLanguagesInquiry}>
                          {content.programmingLanguagesButton[lang]}
                        </button>
                        <button
                          type='button'
                          className='mt-3 bg-milkyWhite text-gray-900 px-2 py-2 rounded-xl text-left hover:bg-gray-700 active:bg-milkyWhite'
                          onClick={handleNewProjectProcessInquiry}>
                          {content.newProjectProcessButton[lang]}
                        </button>
                      </div>}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div key={chatLog.length} className='flex justify-start'>
                  <div className='rounded-lg px-4 py-2 text-gray-400 max-w-sm'>
                    <TypingAnimation />
                  </div>
                </div>
              )}
            </div>
          </div>
          <form onSubmit={handleSubmit} className='py-3 px-2'>
            <div className='flex gap-2'>
              <input type='text'
                className='rounded-lg border border-gray-700 bg-white flex-grow px-4 py-2 bg-transparent text-gray-900 focus:outline-none text-sm'
                placeholder={content.inputPlaceholder[lang]}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)} />
              <button
                type='submit'
                className={`${(isLoading || !inputValue) ? 'bg-gray-500 text-gray-400' : 'bg-purple-500 text-white hover:bg-purple-900'} rounded-xl px-4 py-2 font-semibold focus:outline-none  transition-colors duration-300`}
                disabled={isLoading || !inputValue}>
                {content.sendButton[lang]}
              </button>
            </div>
          </form>
        </div>
      )
        :
        clickChatbot && !askedName ? (
          <div className='max-w-[700px] w-[95%] mx-auto fixed flex flex-col py-2 sm:py-4 bg-gray-900 rounded-xl shadow-xl bottom-2 justify-center items-stretch md:mx-0 right-0 left-0 md:left-auto md:right-3 sm:px-4 z-50'>
            <form onSubmit={handleSubmit} className='py-2 px-2'>
              <div className='flex flex-col gap-2'>
                <StaticImage
                  src='../img/chatbot.png'
                  placeholder='none'
                  aspectRatio={16 / 9}
                  className='w-[30%] sm:w-1/4 text-center pb-8 md:mb-3 mx-auto object-center my-2'
                  alt='Customer service Icon'
                />
                {INTRO_MESSAGE[lang].map(s => (
                  <div key={s.substring(0, 5)} className='text-gray-400 mb-2 text-sm sm:text-base'>{s}</div>
                ))}
                <Link to='/term-of-use-chatbot' className='text-gray-400 mb-2 text-sm sm:text-base hover:text-purple-500 active:text-gray-400'>{content.termsOfUse[lang]}</Link>
                <div className=' text-white font-bold'>{content.namePrompt[lang]}</div>
                <input type='text'
                  className='rounded-lg border border-gray-700 bg-white flex-grow px-2 py-1 bg-transparent text-gray-900 focus:outline-none'
                  placeholder={content.nameInputPlaceholder[lang]}
                  value={name}
                  onChange={(e) => setName(e.target.value)} />
                <button type='submit'
                  className='text-sm bg-purple-500 rounded-xl px-4 py-2 mb-2 text-white font-semibold focus:outline-none hover:bg-purple-600 transition-colors duration-300'>
                  {content.sendButton[lang]}
                </button>
                <button type='button'
                  className='text-sm bg-gray-700 rounded-xl px-4 py-2 text-white font-semibold focus:outline-none hover:bg-purple-600 transition-colors duration-300'
                  onClick={skipName}>
                  {content.skipNameButton[lang]}
                </button>
              </div>
            </form>
          </div>
        )
          :
          <div className='flex items-end mr-2 animate-bounce-slow relative'>
            <div className='bg-white border rounded-lg shadow-lg px-2 py-2 text-sm'>
              <p>{content.chatbotIntro[lang]}</p>
            </div>
            <button type='button' onClick={() => setClickChatbot(!clickChatbot)}>
              <StaticImage src='../img/chatbot.png'
                placeholder='none'
                className='hover:saturate rounded-full object-cover bg-white shadow-2xl'
                height={80} width={80}
                alt='1F8 Logo' />
            </button>
            <button
              type='button'
              className='absolute bottom-0 -right-2 bg-white text-gray-900 rounded-full shadow-lg border w-8 h-8'
              onClick={() => setIsVisible(false)}>
              ✖️
            </button>
          </div>}
    </>
  )
}